// Table cell formatting shared by the Data Grid tables and the plain admin tables: values by their
// declared format (and a row's own currency where a column names one), status badges, progress
// bars, benchmark warnings, and the change vs the comparison period next to a value.
import { useState, type ReactNode } from 'react';
import { formatValue } from '../../lib/format';
import { StatusBadge } from '../Badges';
import { Icon } from '../Icon';
import { SourceIcons } from '../SourceIcons';

export interface Col {
  key: string;
  label: string;
  format?: string;
  currency?: string | null;
  numeric?: boolean;
  emphasis?: boolean;
  benchmark?: { min: number; max: number };
  /** Metric id behind the column; keys the row's per-metric `changes`. */
  metric?: string;
  /** "down" when a fall is good (e.g. return rate). */
  direction?: string;
  /** Row field holding each row's own currency (e.g. a store's local currency). */
  currencyKey?: string;
}

export const NUMERIC = new Set(['currency', 'integer', 'percent', 'ratio', 'days', 'decimal', 'rate', 'number', 'duration']);

/** A long value (e.g. a link) with a button that copies it. */
function CopyText({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      window.prompt('Copy this link', value);
    }
  };
  return (
    <span className="copy-text">
      <span className="copy-value mono" title={value}>
        {value}
      </span>
      <button type="button" className="btn sm" onClick={copy} aria-label="Copy link">
        <Icon name={copied ? 'check' : 'copy'} size={13} />
        {copied ? 'Copied' : 'Copy'}
      </button>
    </span>
  );
}

/** Thumbnails that failed to load this session (tables re-render cells; don't retry them). */
const failedImages = new Set<string>();

export function renderCell(col: Col, v: unknown, row?: Record<string, unknown>): ReactNode {
  if (col.format === 'status') return <StatusBadge status={v as string} />;
  // A post's thumbnail (loaded without sending this page's address to the platform).
  if (col.format === 'image') {
    return typeof v === 'string' && /^https:\/\//.test(v) && !failedImages.has(v) ? (
      <img
        className="cell-thumb"
        src={v}
        alt=""
        loading="lazy"
        referrerPolicy="no-referrer"
        // Platform image links expire; an unloadable thumbnail becomes the empty placeholder.
        onError={(e) => {
          failedImages.add(v);
          e.currentTarget.removeAttribute('src');
          e.currentTarget.classList.add('empty');
        }}
      />
    ) : (
      <span className="cell-thumb empty" aria-hidden />
    );
  }
  // A data source (connector id) as its mark, e.g. a post's platform.
  if (col.format === 'source') return typeof v === 'string' && v ? <SourceIcons sources={[v]} /> : <span className="muted">—</span>;
  // An outside link (https only), opened in a new tab.
  if (col.format === 'link') {
    return typeof v === 'string' && /^https:\/\//.test(v) ? (
      <a className="cell-link" href={v} target="_blank" rel="noopener noreferrer">
        Open <Icon name="link" size={12} />
      </a>
    ) : (
      <span className="muted">—</span>
    );
  }
  if (col.format === 'copy') return typeof v === 'string' && v ? <CopyText value={v} /> : <span className="muted">—</span>;
  if (col.format === 'progress') {
    const p = Math.max(0, Math.min(1, Number(v ?? 0)));
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div className="progress" style={{ flex: 1 }} role="progressbar" aria-valuenow={Math.round(p * 100)} aria-valuemin={0} aria-valuemax={100}>
          <div style={{ width: `${p * 100}%` }} />
        </div>
        <span className="muted">{Math.round(p * 100)}%</span>
      </div>
    );
  }
  // A note in place of a number (e.g. "No data" before the imported history) is shown as text.
  if (typeof v === 'string' && (NUMERIC.has(col.format ?? '') || col.format === 'signed_percent') && Number.isNaN(Number(v))) return <span className="muted">{v}</span>;
  const currency = col.currencyKey && row ? ((row[col.currencyKey] as string | undefined) ?? null) : col.currency;
  const text = formatValue(v, col.format, { currency });
  if (col.benchmark && typeof v === 'number' && v < col.benchmark.min) {
    return (
      <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }} title={`Below benchmark ${formatValue(col.benchmark.min, col.format)}–${formatValue(col.benchmark.max, col.format)}`}>
        <span style={{ color: 'var(--serious)' }}>
          <Icon name="arrowDown" size={12} label="Below benchmark" />
        </span>
        {text}
      </span>
    );
  }
  return text;
}

/** Change vs the comparison period, shown next to a value when the row carries `changes`. */
export function CellChange({ v, direction }: { v: unknown; direction?: string }) {
  if (typeof v !== 'number') return null;
  const flat = Math.abs(v) < 0.0005;
  const good = (v > 0 && direction !== 'down') || (v < 0 && direction === 'down');
  return (
    <span className={`cell-delta ${flat ? 'delta-flat' : good ? 'delta-good' : 'delta-bad'}`}>
      <span className="sr-only">{v > 0 ? 'Up' : v < 0 ? 'Down' : 'Flat'} </span>
      <span aria-hidden>{v > 0 ? '↑' : v < 0 ? '↓' : '→'}</span> {Math.abs(v * 100).toFixed(Math.abs(v) >= 1 ? 0 : 1)}%
    </span>
  );
}


export const isNumericCol = (c: Col) => c.numeric ?? NUMERIC.has(c.format ?? '');
